import { faPen, faRefresh, faXmark } from '@fortawesome/pro-regular-svg-icons';
import { faArrowUp } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { chain } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import AddProjectDomainDialog from '../../../components/muiLegacy/Dialogs/AddProjectDomainDialog';
import { KeywordColumnPickerBag } from '../../../components/muiLegacy/Tables/KeywordTable/KeywordTableColumnPicker/useKeywordColumnPicker';
import { KeywordTableState } from '../../../components/muiLegacy/Tables/KeywordTable/logic/useKeywordTableState';
import VoxelAlert from '../../../components/voxel/Alert/VoxelAlert';
import VoxelLoadingSpinner from '../../../components/voxel/Animation/VoxelLoadingSpinner';
import VoxelIconButton from '../../../components/voxel/Buttons/VoxelIconButton';
import VoxelVerticalDrawer from '../../../components/voxel/Drawers/VoxelVerticalDrawer';
import VoxelSwitch from '../../../components/voxel/Forms/VoxelSwitch';
import { VoxelPopover } from '../../../components/voxel/Popover/VoxelPopover';
import { VoxelPopoverContent } from '../../../components/voxel/Popover/VoxelPopoverContent';
import { VoxelPopoverTrigger } from '../../../components/voxel/Popover/VoxelPopoverTrigger';
import VoxelText from '../../../components/voxel/Text/VoxelText';
import VoxelTooltipShort from '../../../components/voxel/Tooltip/VoxelTooltipShort';
import { DomainGq } from '../../../graphql/projects/models/DomainGq';
import { DomainTypeGq } from '../../../graphql/projects/models/DomainTypeGq';
import { ProjectGq } from '../../../graphql/projects/models/ProjectGq';
import { projectsApiService } from '../../../graphql/projects/projectsApiService';
import DomainItem from './DomainItem';
import EditCompetitorsForm from './EditCompetitorsForm';
import UpdateTop10TrafficDomains from './UpdateTop10TrafficDomainsDialog';
import moment from 'moment';

export interface DomainSectionProps {
  project: ProjectGq;
  reload: () => Promise<any>;
  columnPickerBag: KeywordColumnPickerBag;
  open?: boolean;
  keywordTableState: KeywordTableState;
}

export const DomainSection: React.FC<DomainSectionProps> = ({
  project,
  reload,
  columnPickerBag,
  open,
  keywordTableState,
}) => {
  const { result: loadingResults } = projectsApiService.queries.getProjectStatus(
    { projectId: project.id },
    { fetchPolicy: 'cache-only' } // We handle the polling/loading in ProjectPage.tsx
  );

  // Use automatic domains switch
  const [useAutomaticDomainsFE, setUseAutomaticDomainsFE] = useState(project.useAutomaticDomains);
  const updateUseAutomaticMutation =
    projectsApiService.mutations.projects.useUpdateProjectUseAutomatic();
  const handleUpdateUseAutomatic = (useAutomatic: boolean) => {
    setUseAutomaticDomainsFE(useAutomatic);
    updateUseAutomaticMutation({
      id: project.id,
      useAutomaticDomains: useAutomatic,
    }).then(() => reload());
  };

  // Dialogs
  const [addProjectDomainOpen, setAddProjectDomainOpen] = useState(false);
  const [updateTop10TrafficDomainsOpen, setUpdateTop10TrafficDomainsOpen] = useState(false);
  const [addCompetitorsOpen, setAddCompetitorsOpen] = useState(false);

  // Automatic opening of project domain
  useEffect(() => {
    const domainLocalStorageKey = `hideMissingClientDomain-${project.id}`;
    const hideClientDomain: boolean = JSON.parse(localStorage.getItem(domainLocalStorageKey));
    if (
      !!project.serpTriggered &&
      !project.domains?.some((d) => d.type === DomainTypeGq.PROJECT) &&
      !hideClientDomain
    )
      setAddProjectDomainOpen(true);
  }, [!!project.serpTriggered, !project.domains?.some((d) => d.type === DomainTypeGq.PROJECT)]);

  // Delete domain
  const [deletingIds, setDeletingIds] = React.useState<number[]>([]);
  const deleteMutation = projectsApiService.mutations.domains.useDeleteDomain();
  const handleDelete = (domain: DomainGq) => {
    setDeletingIds((prev) => [...prev, domain.id]);
    deleteMutation({ id: domain.id, projectId: domain.parentProjectId })
      .finally(reload)
      .finally(() => setDeletingIds((prev) => prev.filter((id) => id !== domain.id)));
  };

  const projectDomain = project.domains.find((d) => d.type === DomainTypeGq.PROJECT);
  const competitorDomains = project.domains.filter((d) => d.type === DomainTypeGq.COMPETITOR);
  // Project domain and competitor domains
  const manualDomains = useMemo(
    () => project.domains.filter((d) => d.type !== DomainTypeGq.AUTOMATIC),
    [project.domains]
  );
  const automaticDomains = project.domains.filter((d) => d.type === DomainTypeGq.AUTOMATIC);

  const createDomainsMutation = projectsApiService.mutations.domains.useCreateDomains();
  const handleAddAutomaticToCompetitors = (domain: DomainGq) => {
    createDomainsMutation({
      projectId: project.id,
      domains: [
        {
          protocol: domain.protocol,
          domain: domain.hostName,
          type: DomainTypeGq.COMPETITOR,
        },
      ],
    }).then(() => reload());
  };

  // Not open
  if (!open) {
    return (
      <>
        {addProjectDomainOpen && (
          <AddProjectDomainDialog
            project={project}
            onSuccess={(domainId) =>
              reload().then(() => {
                setAddProjectDomainOpen(false);
                columnPickerBag.showProjectDomain(domainId);
              })
            }
            close={() => {
              setAddProjectDomainOpen(false);
            }}
          />
        )}
      </>
    );
  }
  // Default screen
  return (
    <VoxelVerticalDrawer
      bgColorClass="bg-white"
      shadowClass="shadow-none"
      closedWidthClass="w-80"
      closedMinWidthClass="min-w-80"
      zIndexClass="z-20"
    >
      <div className={`h-full relative`}>
        <VoxelIconButton
          onClick={() =>
            keywordTableState.setTableUIState((prev) => ({
              ...prev,
              showDomainsPanel: false,
            }))
          }
          icon={<FontAwesomeIcon icon={faXmark} size="lg" />}
          size="md"
          tooltip="Close"
          color="white"
          className="absolute top-2 right-2"
        />
        {/* Dialogs */}
        {addProjectDomainOpen && (
          <AddProjectDomainDialog
            project={project}
            onSuccess={(domainId) =>
              reload().then(() => {
                setAddProjectDomainOpen(false);
                columnPickerBag.showProjectDomain(domainId);
              })
            }
            close={() => setAddProjectDomainOpen(false)}
          />
        )}
        {updateTop10TrafficDomainsOpen && (
          <UpdateTop10TrafficDomains
            project={project}
            onSuccess={(domainId) =>
              reload().then(() => {
                setUpdateTop10TrafficDomainsOpen(false);
              })
            }
            close={() => setUpdateTop10TrafficDomainsOpen(false)}
          />
        )}
        <div
          className={`
            w-full max-h-[calc(100vh-2*32px)]
            flex flex-col gap-8
            overflow-y-auto
            py-10
          `}
        >
          {/* Project domain */}
          <div className="flex flex-col gap-3 pl-6 pr-5">
            <VoxelText variant="bread-semibold">Project domain</VoxelText>
            {projectDomain === undefined && (
              <VoxelAlert
                size="xs"
                severity="error"
                iconSize="lg"
                onClickTitle={'Add'}
                onClick={() => setAddProjectDomainOpen(true)}
              >
                Project is missing a project domain. Add one before adding competitors.
              </VoxelAlert>
            )}
            {projectDomain !== undefined && (
              <div className="flex flex-col gap-3">
                <DomainItem
                  key={projectDomain.id}
                  domain={projectDomain}
                  deletingIds={deletingIds}
                  reload={reload}
                  columnPickerBag={columnPickerBag}
                  handleDelete={handleDelete}
                />
              </div>
            )}
          </div>
          {/* Competitior list*/}
          {projectDomain !== undefined && (
            <div className="flex flex-col gap-3 pl-6 pr-5">
              <div className="flex-grow flex flex-row items-center gap-2">
                <VoxelTooltipShort message="Permanent competitors list">
                  <VoxelText variant="bread-semibold">Competitors</VoxelText>
                </VoxelTooltipShort>
                <VoxelPopover
                  placement="right-start"
                  offsetPx={10}
                  open={addCompetitorsOpen}
                  onOpenChange={setAddCompetitorsOpen}
                >
                  <VoxelPopoverTrigger enableActive>
                    <VoxelIconButton
                      tooltip={
                        !!competitorDomains.length
                          ? 'Edit list of permanent competitors'
                          : 'Add list of permanent competitors'
                      }
                      icon={<FontAwesomeIcon icon={faPen} size="sm" />}
                      size="xxs"
                      color="white"
                      onClick={() => setAddCompetitorsOpen(!addCompetitorsOpen)}
                    />
                  </VoxelPopoverTrigger>
                  <VoxelPopoverContent>
                    <EditCompetitorsForm
                      project={project}
                      onSuccess={(domainIds) =>
                        reload().then(() => {
                          setAddCompetitorsOpen(false);
                        })
                      }
                      handleClose={() => setAddCompetitorsOpen(false)}
                    />
                  </VoxelPopoverContent>
                </VoxelPopover>
              </div>
              {!competitorDomains.length && projectDomain !== undefined && (
                <VoxelText variant="table" color="slight" italic className="mt-1">
                  Project has no permanent competitors
                </VoxelText>
              )}
              <div className="flex flex-col gap-3">
                {chain(competitorDomains)
                  .map((d) => {
                    return (
                      <DomainItem
                        key={d.id}
                        domain={d}
                        reload={reload}
                        deletingIds={deletingIds}
                        columnPickerBag={columnPickerBag}
                        handleDelete={handleDelete}
                      />
                    );
                  })
                  .value()}
              </div>
            </div>
          )}
          {/* Top 10 traffic domains */}
          {projectDomain !== undefined && (
            <>
              <div className="flex flex-col gap-3 pl-6 pr-5 mt-1">
                <div className="flex flex-row items-center justify-between">
                  <div className="flex flex-row items-center gap-2">
                    <VoxelTooltipShort message="Top 10 traffic domains based on current rank data">
                      <VoxelText
                        variant="bread-semibold"
                        color="medium"
                        disabled={
                          !project?.serpAutomaticDomainComputed &&
                          !loadingResults?.automaticDomainsLoading
                        }
                      >
                        Automatic domains
                      </VoxelText>
                    </VoxelTooltipShort>
                    <VoxelIconButton
                      disabled={
                        !project.serpAutomaticDomainComputed ||
                        moment
                          .utc(project.serpAutomaticDomainComputed)
                          .isAfter(moment.utc().add(-30, 'minutes'))
                      }
                      icon={<FontAwesomeIcon icon={faRefresh} size="sm" />}
                      tooltip="An update may be needed in case a lot of keywords have been archived since adding rank data"
                      onClick={() => setUpdateTop10TrafficDomainsOpen(true)}
                      size="xxs"
                      color="light-gray"
                    />
                    {loadingResults?.automaticDomainsLoading && (
                      <VoxelLoadingSpinner sizeClass="w-3.5 h-3.5" />
                    )}
                  </div>
                  <VoxelTooltipShort message="Include automatic domains in charts and tables. Warning! These may be updated if the underlying data changes.">
                    <span>
                      <VoxelSwitch
                        value={useAutomaticDomainsFE}
                        onChange={() => handleUpdateUseAutomatic(!useAutomaticDomainsFE)}
                        size="sm"
                        title="Use"
                      />
                    </span>
                  </VoxelTooltipShort>
                </div>
                {!project?.serpAutomaticDomainComputed && !!loadingResults?.rankingsLoading && (
                  <VoxelAlert severity="info" size="xs" iconSize="sm">
                    Waiting for rank data to complete
                  </VoxelAlert>
                )}
                <div className="flex flex-col gap-3">
                  {chain(automaticDomains)
                    .orderBy([(d) => d.automaticDomainOrder, (d) => d.hostName], ['asc', 'asc'])
                    .map((d) => {
                      const alreadyAdded = manualDomains.some(
                        (m) => m.protocol + m.hostName === d.protocol + d.hostName
                      );
                      return (
                        <div
                          key={d.id}
                          className="flex flex-row items-center justify-between flex-nowrap"
                        >
                          <VoxelText variant="form-caption" color="dark">
                            <span className="mr-1">{d.automaticDomainOrder}</span> {d.hostName}
                          </VoxelText>
                          <VoxelIconButton
                            className="-my-1"
                            size="xs"
                            color="white"
                            icon={<FontAwesomeIcon icon={faArrowUp} size="xs" />}
                            tooltip={
                              alreadyAdded
                                ? 'Already added to permanent competitors list'
                                : 'Add to permanent competitors list'
                            }
                            disabled={alreadyAdded}
                            onClick={() => handleAddAutomaticToCompetitors(d)}
                          />
                        </div>
                      );
                    })
                    .value()}
                </div>
              </div>
              {/* <div className='px-4'>
                <VoxelAlert severity='success' size='xs' fontAIconName={faLightbulb} iconSize='lg' onClose={() => { }} hideClose={false}>
                  <span className='text-xs'>Turn automatic domains into permanent competitors by clicking the arrow <FontAwesomeIcon icon={faArrowUp} size='xs' className='px-0.5' /> button. </span>
                </VoxelAlert>
              </div> */}
            </>
          )}
        </div>
      </div>
    </VoxelVerticalDrawer>
  );
};

export default DomainSection;
